import React, { useState, useEffect } from "react";
import "./contactform.scss";
import { Modal, Button } from "antd";
import { getVisitorCountryInfo, getAllCountryCallingCodes } from "../../utils/countryUtils";

const ContactForm = ({ heading, description, reusing, close, formType, fields }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [visitorInfo, setVisitorInfo] = useState({ iso2: "IN", countryName: "India", callingCode: "+91", flagEmoji: "🇮🇳" });
  const [callingCodes, setCallingCodes] = useState([]);
  const [selectedIso2, setSelectedIso2] = useState("IN");

  useEffect(() => {
    const init = async () => {
      try {
        const [info, codes] = await Promise.all([
          getVisitorCountryInfo(),
          getAllCountryCallingCodes()
        ]);
        setVisitorInfo(info);
        setCallingCodes(codes);
        const hasIso2 = codes.find((c) => c.iso2 === info.iso2);
        setSelectedIso2(hasIso2 ? info.iso2 : (codes[0]?.iso2 || "IN"));
      } catch (e) {
        setSelectedIso2("IN");
      }
    };
    init();
  }, []);

  const showModal = (message) => {
    setModalMessage(message);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  // Function to format form data as HTML
  const formatDataAsHTML = (formData, formType) => {
    // Extract form data
    const name = formData.get('name') || 'Not provided';
    const email = formData.get('email') || 'Not provided';
    const phone = formData.get('phone') || formData.get('contactnumber') || 'Not provided';
    const message = formData.get('message') || formData.get('comment') || 'No message provided';
    
    // Get country and calling code info
    const selectedDialCode = formData.get('selectedCountryCode') || '';
    const geoCountry = formData.get('visitorCountryName') || 'Unknown';
    const geoCountryCode = formData.get('visitorCountryISO2') || '';
    const ipAddress = 'Unknown'; // Could be enhanced to get actual IP if needed
    
    // Determine title based on form type
    let title = 'New Contact Us Form Submission';
    if (formType === 'doctor') {
      title = 'New Doctor/Consultant Application';
    } else if (formType === 'partner') {
      title = 'New Partner Clinic/Hospital Application';
    }

    const htmlContent = `
      <h2>${title}</h2>
      <hr>
      <p><strong>Name:</strong> ${name}</p>
      <p><strong>Email:</strong> ${email}</p>
      <p><strong>Mobile Number:</strong> ${phone !== 'Not provided' ? `${selectedDialCode} ${phone}` : 'Not provided'}</p>
      <p><strong>Detected Country:</strong> ${geoCountry || 'Unknown'}${geoCountryCode ? ` (${geoCountryCode})` : ''}</p>
      <p><strong>Comment/Questions:</strong></p>
      <p>${message || 'No comment provided'}</p>
      <hr>
      <p><em>This message was sent from the AxonCare contact us page.</em></p>
    `;

    return htmlContent;
  };

  function Submit(e) {
    e.preventDefault();

    const formEle = document.querySelector("form");
    const formDatab = new FormData(formEle);
    
    // Add form type to identify which form was submitted
    if (formType) {
      formDatab.append("formType", formType);
    }

    // Add visitor country info
    if (visitorInfo) {
      formDatab.append("visitorCountryISO2", visitorInfo.iso2 || "");
      formDatab.append("visitorCountryName", visitorInfo.countryName || "");
      formDatab.append("visitorCountryCallingCode", visitorInfo.callingCode || "");
    }

    // Add selected calling code from dropdown
    const selectedOption = callingCodes.find((c) => c.iso2 === selectedIso2);
    if (selectedOption?.code) {
      formDatab.append("selectedCountryCode", selectedOption.code);
    }

    // Format the data as HTML
    const htmlData = formatDataAsHTML(formDatab, formType);

    // Determine subject and recipient based on form type
    let subject = "Contact Form Submission";
    let recipient = 
    "axoncare@axonichealth.com";

    if (formType === 'doctor') {
      subject = "New Doctor/Consultant Application";
    } else if (formType === 'partner') {
      subject = "New Partner Clinic/Hospital Application";
    }

    // Prepare the API payload
    const payload = {
      to: recipient,
      from: "info@axonichealth.com",
      subject: subject,
      data: htmlData
    };

    fetch(
      "https://ojw0jjra11.execute-api.ap-south-1.amazonaws.com/prod/sendEmail",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        showModal("Your message was sent successfully!");
        formEle.reset();
      })
      .catch((error) => {
        console.error("Error:", error);
        showModal("Something went wrong. Please try again.");
      });
  }

  // Default fields if none are provided
  const defaultFields = [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "phone", label: "Phone", type: "tel", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "message", label: "Message", type: "textarea", required: false },
  ];

  // Use provided fields or default fields
  const formFields = fields || defaultFields;

  return (
    <>
      <div
        className="form-container"
        style={{
          boxShadow: reusing ? "0px 0px 0px" : "",
          padding: reusing ? "0px" : "",
        }}
      >
        <h2 className="heading contact-form-heading">{heading}</h2>
        <p className="contact-form-description">{description}</p>
        <form className="contact-form" onSubmit={Submit}>
          {formFields.map((field, index) => {
            const isPhone = field.type === "tel" || /phone|contactnumber/i.test(field.name || "");
            if (isPhone) {
              return (
                <div className="form-group phone-group" key={index}>
                  <div className="phone-composite">
                    <select
                      className="country-select"
                      name="countryCode"
                      value={selectedIso2}
                      onChange={(e) => setSelectedIso2(e.target.value)}
                      aria-label="Country calling code"
                    >
                      {callingCodes.map((opt) => (
                        <option key={opt.iso2} value={opt.iso2}>
                          {opt.flagEmoji} {opt.code} {opt.name}
                        </option>
                      ))}
                    </select>
                    <div className="phone-input-wrapper">
                      <input 
                        type="tel" 
                        id={field.name} 
                        required={field.required} 
                        name={field.name} 
                        inputMode="tel"
                        placeholder=" "
                      />
                      <label htmlFor={field.name}>{field.label} {field.required ? "*" : ""}</label>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="form-group" key={index}>
                {field.type === "textarea" ? (
                  <textarea 
                    id={field.name} 
                    rows="4" 
                    name={field.name}
                    required={field.required} 
                  />
                ) : (
                  <input 
                    type={field.type} 
                    id={field.name} 
                    required={field.required} 
                    name={field.name} 
                  />
                )}
                <label htmlFor={field.name}>{field.label} {field.required ? "*" : ""}</label>
              </div>
            );
          })}
          <div className="button-box">
            <button type="submit" className="cta-btn">
              Send Message
            </button>
          </div>
        </form>
      </div>

      <Modal
        title="Notification"
        open={isModalVisible}
        onOk={handleOk}
        footer={null}
      >
        <p>{modalMessage}</p>
        <div style={{ textAlign: "right" }}>
          <button className="cta-btn" onClick={handleOk}>
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ContactForm;
